import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostRescheduleFormProps {
  isSaving: boolean;
  /** Called with an ISO time in the future. */
  onReschedule: (scheduledAt: string) => void;
}

/**
 * Design product-gaps a2: a new time for a failed post. The server moves the post back to
 * `scheduled` and takes no new unit. A time in the past is refused here, before the call.
 */
const PostRescheduleFormComponent: React.FC<PostRescheduleFormProps> = ({ isSaving, onReschedule }) => {
  const intl = useIntl();
  const [value, setValue] = useState('');

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value), []);
  // The picked time as ISO, or null when it is empty, not a date or not in the future.
  const futureIso = useMemo(() => {
    if (value === '') {
      return null;
    }
    const picked = new Date(value);
    return Number.isNaN(picked.getTime()) || picked.getTime() <= Date.now() ? null : picked.toISOString();
  }, [value]);
  const isInFuture = futureIso !== null;

  const submit = useCallback(() => {
    if (futureIso !== null) {
      onReschedule(futureIso);
    }
  }, [futureIso, onReschedule]);

  const label = intl.formatMessage({ id: 'app.sml.promote.post.scheduledAt', defaultMessage: 'Goes out' });

  return (
    <Panel nested className={clsx(styles.reschedule, 'mt-3')}>
      <div className={styles.statLabel}>{label}</div>
      <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
        <input
          type="datetime-local"
          className={styles.handleInput}
          value={value}
          aria-label={label}
          onChange={onChange}
          disabled={isSaving}
        />
        <SMLButton color="primary" omitCap disabled={isSaving || !isInFuture} onClick={submit}>
          {intl.formatMessage({ id: 'app.sml.promote.post.scheduleAgain', defaultMessage: 'Schedule again' })}
        </SMLButton>
      </div>
      <div className={styles.statNote}>
        {value !== '' && !isInFuture
          ? intl.formatMessage({
              id: 'app.sml.promote.post.reschedule.past',
              defaultMessage: 'Pick a time in the future.',
            })
          : intl.formatMessage({
              id: 'app.sml.promote.post.reschedule.help',
              defaultMessage:
                'The post keeps its caption and media. Scheduling it again does not take another scheduled post.',
            })}
      </div>
    </Panel>
  );
};

export const PostRescheduleForm = TypedMemo(PostRescheduleFormComponent);
